import { lazy, PropsWithChildren, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { usePrevious } from 'react-use';

import { ROUTE_PATHS } from '@/shared/consts/routes';

import { IUserRole } from 'api/apiUsers/models';
import { isDsCoverRoute } from 'shared/utils/isDsCoverRoute';
import { useTypedSelector } from 'store';
import { SessionLogoutModal } from 'ui/SessionLogoutModal';
import { Spinner } from 'ui/Spinner/Spinner';
import { ApplicationLayout } from 'widgets/application-layout';

import { TraderRoute } from './TraderRoute';

const Pair = lazy(() => import('pages/pair'));
const Project = lazy(() => import('pages/Project/Project'));
const Wallets = lazy(() => import('pages/Wallets/Wallets'));
const Accounts = lazy(() => import('pages/Account/Accounts'));
const Reports = lazy(() => import('pages/Reports/Reports'));
const Bridge = lazy(() => import('pages/Bridge/Bridge'));
const DSCover = lazy(() => import('pages/DSCover/DSCover'));
const Authentication = lazy(() => import('pages/Authentication/Authentication'));
const Arbitrage = lazy(() => import('pages/Arbitrage/Arbitrage'));
const Balancer = lazy(() => import('pages/Balancer/Balancer'));
const CexPair = lazy(() => import('pages/CexPair'));
const DexPair = lazy(() => import('pages/DexPair/DexPair'));
const SolPair = lazy(() => import('pages/SolPair/SolPair'));
const MexcDistributor = lazy(() => import('pages/MexcDistributor/MexcDistributor'));
const PublicCexMarket = lazy(() => import('pages/PublicCexMarket'));
const PublicClobMarket = lazy(() => import('pages/PublicClobMarket'));
const PublicDexAggregator = lazy(() => import('pages/PublicDexAggregator'));
const PublicDexPool = lazy(() => import('pages/PublicDexPool'));
const PublicStableSwapPool = lazy(() => import('pages/PublicStableSwapPool'));
const PublicSingletonPool = lazy(() => import('pages/PublicSingletonPool'));
const PublicConcentratedPool = lazy(() => import('pages/PublicConcentratedPool'));
const PublicAvalanchePool = lazy(() => import('pages/PublicAvalanchePool'));
const PublicSuiPool = lazy(() => import('pages/PublicSuiPool'));
const PublicTonPool = lazy(() => import('pages/PublicTonPool'));
const PublicTronPool = lazy(() => import('pages/PublicTronPool'));
const PublicNearPool = lazy(() => import('pages/PublicNearPool'));
const PublicAptosPool = lazy(() => import('pages/PublicAptosPool'));
const XnkBot = lazy(() => import('pages/XnkBot/XnkBot'));

type PrivateRouteProps = {
  roles: IUserRole[];
} & PropsWithChildren;

const suspenseFallback = (
  <div className="flex items-center justify-center p-6">
    <Spinner size="medium" />
  </div>
);

function PrivateRoute({ children, roles }: PrivateRouteProps) {
  const user = useTypedSelector(store => store.auth.user);

  if (!user) return <Navigate to={ROUTE_PATHS.login} />;

  if (user && !roles.includes(user.role)) {
    return <Navigate to={'/'} />;
  }

  return <>{children}</>;
}

export function Routing() {
  const location = useLocation();
  const userIsAuthenticated = useTypedSelector(store => Boolean(store.auth.jwt));
  const userRole = useTypedSelector(store => store.auth.user?.role);

  const prevPathname = usePrevious(location.pathname);

  // Theme is automatically initialized from Zustand store (persisted in localStorage)

  if (!userIsAuthenticated) {
    return (
      <Suspense fallback={suspenseFallback}>
        <Routes>
          <Route
            path={ROUTE_PATHS.login}
            element={<Authentication prevPathname={prevPathname} />}
          />
          <Route path="*" element={<Navigate to={ROUTE_PATHS.login} replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (isDsCoverRoute(location.pathname)) {
    return (
      <Suspense fallback={suspenseFallback}>
        <SessionLogoutModal />
        <PrivateRoute roles={['admin']}>
          <DSCover />
        </PrivateRoute>
      </Suspense>
    );
  }

  return (
    <ApplicationLayout>
      <Suspense fallback={suspenseFallback}>
        <SessionLogoutModal />

        <Routes>
          <Route
            path={ROUTE_PATHS.wallets}
            element={
              <PrivateRoute roles={['admin']}>
                <Wallets />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.accounts}
            element={
              <PrivateRoute roles={['admin']}>
                <Accounts />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.reports}
            element={
              <PrivateRoute roles={['admin']}>
                <Reports />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.bridge}
            element={
              <PrivateRoute roles={['admin']}>
                <Bridge />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.mexcDistributor}
            element={
              <TraderRoute>
                <MexcDistributor />
              </TraderRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.balancer}
            element={
              <PrivateRoute roles={['admin']}>
                <Balancer />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicCexMarket}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicCexMarket />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicDexPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicDexPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicDexAggregator}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicDexAggregator />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicClobMarket}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicClobMarket />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicStableSwapPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicStableSwapPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicSingletonPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicSingletonPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicConcentratedPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicConcentratedPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicAvalanchePool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicAvalanchePool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicSuiPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicSuiPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicTonPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicTonPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicTronPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicTronPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicNearPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicNearPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.publicAptosPool}
            element={
              <PrivateRoute roles={['admin']}>
                <PublicAptosPool />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.balancerCreate}
            element={
              <PrivateRoute roles={['admin']}>
                <Balancer />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.project}
            element={
              <PrivateRoute roles={['admin', 'user']}>
                <Project />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.pair}
            element={
              <PrivateRoute roles={['admin']}>
                <Pair />
              </PrivateRoute>
            }
          />

          <Route path={ROUTE_PATHS.dexPair} element={<DexPair />} />
          <Route path={ROUTE_PATHS.solPair} element={<SolPair />} />
          <Route path={ROUTE_PATHS.cexPair} element={<CexPair />} />

          <Route
            path={ROUTE_PATHS.arbitrage}
            element={
              <PrivateRoute roles={['admin']}>
                <Arbitrage />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.xnkBot}
            element={
              <TraderRoute>
                <XnkBot />
              </TraderRoute>
            }
          />

          {userRole === 'admin' ? (
            <Route path="*" element={<Navigate to={ROUTE_PATHS.wallets} replace />} />
          ) : (
            <>
              <Route index element={<></>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
    </ApplicationLayout>
  );
}
